import { apiClient } from './apiClient';

export async function loginHandler(email: string, password: string) {
  try {
    const response = await apiClient.post("/api/auth", { email, password });
    if (!response) return; // 401 handled by apiClient

    const data = await response.json();
    console.log(data);
    return data;
  } catch (error) {
    console.error("Error logging in:", error);
    return error;
  }
}

// Submit the 6-digit code from the user's authenticator app to complete
// a SOFTWARE_TOKEN_MFA challenge. Returns the standard auth result on
// success.
export async function submitMfaCode(
  email: string,
  session: string,
  code: string,
) {
  try {
    const response = await fetch("/api/auth/mfa", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, session, code }),
    });
    const data = await response.json();
    return { ok: response.ok, ...data };
  } catch (error: any) {
    return { ok: false, message: error?.message || "Failed to verify code" };
  }
}

// First-time TOTP setup, step 1: ask Cognito to generate a secret. We get
// back an otpauth:// URL that we render as a QR code for the user to scan
// with their authenticator app. The rotated session token must be passed
// back to confirmMfaSetup() — Cognito invalidates the old one.
export async function associateMfa(email: string, session: string) {
  try {
    const response = await fetch("/api/auth/mfa-associate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, session }),
    });
    const data = await response.json();
    return { ok: response.ok, ...data };
  } catch (error: any) {
    return { ok: false, message: error?.message || "Failed to start setup" };
  }
}

// First-time TOTP setup, step 2: user has scanned the QR code in their
// authenticator app, types the first 6-digit code, we verify and complete
// the login (sets cookies on success).
export async function confirmMfaSetup(
  email: string,
  session: string,
  code: string,
) {
  try {
    const response = await fetch("/api/auth/mfa-verify-setup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, session, code }),
    });
    const data = await response.json();
    return { ok: response.ok, ...data };
  } catch (error: any) {
    return { ok: false, message: error?.message || "Failed to complete setup" };
  }
}

// Trade the long-lived refresh token (stored as httpOnly cookie) for a
// fresh access token. Backend rotates the `token` exp cookie too, so
// scheduleSessionExpiry will pick up the new expiration on next reschedule.
export async function refreshAccessToken(): Promise<{ ok: boolean; exp?: number }> {
  try {
    const response = await fetch("/api/refreshToken", {
      method: "POST",
      credentials: "include",
    });
    if (!response.ok) return { ok: false };
    const data = await response.json();
    return { ok: data.message === "Success", exp: data.exp };
  } catch (error) {
    console.error("Error refreshing token:", error);
    return { ok: false };
  }
}
